import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { marketDataService } from '@/services/marketDataService'
import {
  ChartBarIcon,
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlayIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline'
import { formatDistanceToNow, format } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import toast from 'react-hot-toast'

type SortField = 'symbol' | 'exchange' | 'current_price' | 'change_percent' | 'volume' | 'timestamp_tz'
type SortDirection = 'asc' | 'desc'

export default function MarketDataPage() {
  const [selectedSymbol, setSelectedSymbol] = useState<string>('all')
  const [selectedExchange, setSelectedExchange] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isCollecting, setIsCollecting] = useState(false)
  const [sortField, setSortField] = useState<SortField>('timestamp_tz')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // 查询最新行情数据
  const { data: latestPrices, isLoading: isLoadingPrices, refetch: refetchPrices } = useQuery({
    queryKey: ['market-prices', 'latest'],
    queryFn: () => marketDataService.getLatestPrices(),
    refetchInterval: 5000, // 每5秒自动刷新
  })

  // 查询行情统计
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['market-stats'],
    queryFn: () => marketDataService.getMarketStats(),
    refetchInterval: 10000, // 每10秒自动刷新
  })

  // 查询监控状态
  const { data: monitoring, isLoading: isLoadingMonitoring } = useQuery({
    queryKey: ['market-monitoring'],
    queryFn: () => marketDataService.getMonitoringStatus(),
    refetchInterval: 30000, // 每30秒自动刷新
    retry: 1, // 只重试1次
  })

  // 排序和过滤数据
  const { filteredPrices, paginatedPrices, totalPages } = useMemo(() => {
    let filtered = latestPrices?.data?.prices?.filter((price: any) => {
      const matchesSymbol = selectedSymbol === 'all' || price.symbol === selectedSymbol
      const matchesExchange = selectedExchange === 'all' || price.exchange === selectedExchange
      const matchesSearch = !searchQuery || 
        price.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
        price.exchange.toLowerCase().includes(searchQuery.toLowerCase())
      
      return matchesSymbol && matchesExchange && matchesSearch
    }) || []

    // 排序
    filtered.sort((a: any, b: any) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      // 处理时间戳
      if (sortField === 'timestamp_tz') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    // 分页
    const total = Math.ceil(filtered.length / pageSize)
    const start = (currentPage - 1) * pageSize
    const paginated = filtered.slice(start, start + pageSize)

    return {
      filteredPrices: filtered,
      paginatedPrices: paginated,
      totalPages: total
    }
  }, [latestPrices, selectedSymbol, selectedExchange, searchQuery, sortField, sortDirection, currentPage, pageSize])

  // 处理排序
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
    setCurrentPage(1) // 重置到第一页
  }

  // 排序图标
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUpIcon className="h-4 w-4 text-primary-600" />
    ) : (
      <ArrowDownIcon className="h-4 w-4 text-primary-600" />
    )
  }

  // 获取唯一的交易对和交易所列表
  const symbols = Array.from(new Set(latestPrices?.data?.prices?.map((p: any) => p.symbol) || []))
  const exchanges = Array.from(new Set(latestPrices?.data?.prices?.map((p: any) => p.exchange) || []))

  // 手动触发采集
  const triggerCollectionMutation = useMutation({
    mutationFn: () => marketDataService.triggerCollection(),
    onMutate: () => {
      setIsCollecting(true)
      toast.loading('正在触发数据采集...', { id: 'trigger-collection' })
    },
    onSuccess: (response) => {
      setIsCollecting(false)
      toast.success('数据采集已成功触发！', { id: 'trigger-collection' })
      
      // 显示采集结果
      if (response.data?.data) {
        const stats = response.data.data
        toast.success(
          `采集完成：成功 ${stats.successful_collections}/${stats.total_symbols} 个交易对，` +
          `插入 ${stats.total_records_inserted} 条记录`,
          { duration: 5000 }
        )
      }
      
      // 刷新数据
      setTimeout(() => {
        refetchPrices()
      }, 2000)
    },
    onError: (error: any) => {
      setIsCollecting(false)
      toast.error(error.message || '触发数据采集失败', { id: 'trigger-collection' })
    },
  })

  const handleTriggerCollection = () => {
    triggerCollectionMutation.mutate()
  }

  const isLoading = isLoadingPrices || isLoadingStats || isLoadingMonitoring

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="card">
                <div className="card-body">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">行情管理</h1>
            <p className="page-subtitle">
              实时监控和管理市场行情数据
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleTriggerCollection}
              disabled={isCollecting}
              className="btn-primary flex items-center space-x-2"
            >
              <PlayIcon className="h-5 w-5" />
              <span>{isCollecting ? '采集中...' : '手动采集'}</span>
            </button>
            <button
              onClick={() => refetchPrices()}
              className="btn-secondary flex items-center space-x-2"
            >
              <ArrowPathIcon className="h-5 w-5" />
              <span>刷新数据</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    总记录数
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {stats?.data?.total_records?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    活跃交易对
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {symbols.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    交易所数量
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {exchanges.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {monitoring?.data?.all_healthy ? (
                  <CheckCircleIcon className="h-6 w-6 text-success-600" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 text-danger-600" />
                )}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    系统状态
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {!monitoring?.data ? '未知' : 
                     monitoring.data.all_healthy ? '正常' : '异常'}
                  </dd>
                  {!monitoring?.data?.all_healthy && monitoring?.data?.alerts && (
                    <dd className="text-xs text-danger-600 mt-1">
                      {monitoring.data.alerts.length} 个告警
                    </dd>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monitoring alerts */}
      {monitoring?.data?.alerts && monitoring.data.alerts.length > 0 && (
        <div className="mb-8">
          <div className="card border-l-4 border-warning-500">
            <div className="card-body">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-6 w-6 text-warning-600 mt-0.5" />
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-warning-800">
                    监控告警 ({monitoring.data.alerts.length})
                  </h3>
                  <div className="mt-2 text-sm text-warning-700">
                    <ul className="list-disc list-inside space-y-1">
                      {monitoring.data.alerts.map((alert: any, index: number) => (
                        <li key={index}>
                          <span className="font-medium">{alert.symbol}</span>: {alert.message}
                          {alert.severity === 'CRITICAL' && (
                            <span className="ml-2 badge-danger text-xs">严重</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-3 text-xs text-warning-600">
                    💡 提示: 如果数据长时间未更新，请检查数据采集服务是否正常运行，或手动触发数据采集。
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No data warning */}
      {!isLoading && (!latestPrices?.data?.prices || latestPrices.data.prices.length === 0) && (
        <div className="mb-8">
          <div className="card border-l-4 border-primary-500">
            <div className="card-body">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-6 w-6 text-primary-600 mt-0.5" />
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-primary-800">
                    暂无行情数据
                  </h3>
                  <div className="mt-2 text-sm text-primary-700">
                    <p>系统中还没有行情数据。这可能是因为：</p>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      <li>数据采集服务尚未启动</li>
                      <li>数据库中没有历史数据</li>
                      <li>数据采集配置有误</li>
                    </ul>
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={handleTriggerCollection}
                      disabled={isCollecting}
                      className="btn-primary text-sm flex items-center space-x-2"
                    >
                      <PlayIcon className="h-4 w-4" />
                      <span>{isCollecting ? '采集中...' : '手动触发数据采集'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-8">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="搜索交易对或交易所..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Symbol filter */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FunnelIcon className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={selectedSymbol}
                onChange={(e) => setSelectedSymbol(e.target.value)}
                className="input pl-10"
              >
                <option value="all">所有交易对</option>
                {symbols.map((symbol) => (
                  <option key={symbol} value={symbol}>
                    {symbol}
                  </option>
                ))}
              </select>
            </div>

            {/* Exchange filter */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FunnelIcon className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={selectedExchange}
                onChange={(e) => setSelectedExchange(e.target.value)}
                className="input pl-10"
              >
                <option value="all">所有交易所</option>
                {exchanges.map((exchange) => (
                  <option key={exchange} value={exchange}>
                    {exchange}
                  </option>
                ))}
              </select>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>显示 {filteredPrices.length} 条结果</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="input py-1 text-sm"
              >
                <option value={10}>每页 10 条</option>
                <option value={20}>每页 20 条</option>
                <option value={50}>每页 50 条</option>
                <option value={100}>每页 100 条</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Market prices table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            最新行情数据
          </h3>
          <div className="text-sm text-gray-500">
            {filteredPrices.length > 0 && (
              <>
                第 {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredPrices.length)} 条，
                共 {filteredPrices.length} 条
              </>
            )}
          </div>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th 
                    className="cursor-pointer hover:bg-gray-50 select-none px-6 py-3"
                    onClick={() => handleSort('symbol')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>交易对</span>
                      <SortIcon field="symbol" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-gray-50 select-none px-6 py-3"
                    onClick={() => handleSort('exchange')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>交易所</span>
                      <SortIcon field="exchange" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-gray-50 select-none px-6 py-3"
                    onClick={() => handleSort('current_price')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>当前价格</span>
                      <SortIcon field="current_price" />
                    </div>
                  </th>
                  <th className="px-6 py-3">买价/卖价</th>
                  <th 
                    className="cursor-pointer hover:bg-gray-50 select-none px-6 py-3"
                    onClick={() => handleSort('change_percent')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>涨跌幅</span>
                      <SortIcon field="change_percent" />
                    </div>
                  </th>
                  <th className="px-6 py-3">最高/最低</th>
                  <th 
                    className="cursor-pointer hover:bg-gray-50 select-none px-6 py-3"
                    onClick={() => handleSort('volume')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>成交量</span>
                      <SortIcon field="volume" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-gray-50 select-none px-6 py-3"
                    onClick={() => handleSort('timestamp_tz')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>更新时间</span>
                      <SortIcon field="timestamp_tz" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedPrices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                ) : (
                  paginatedPrices.map((price: any) => (
                    <tr key={`${price.symbol}-${price.exchange}-${price.timestamp}`} className="hover:bg-gray-50">
                      <td className="font-medium text-gray-900 px-6 py-4">{price.symbol}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {price.exchange}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">
                            ${price.current_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className={`text-xs ${price.change_amount >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                            {price.change_amount >= 0 ? '+' : ''}
                            ${Math.abs(price.change_amount)?.toFixed(2)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col text-xs text-gray-600">
                          <span>买: ${price.bid_price?.toFixed(2) || '-'}</span>
                          <span>卖: ${price.ask_price?.toFixed(2) || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            price.change_percent >= 0
                              ? 'bg-success-100 text-success-800'
                              : 'bg-danger-100 text-danger-800'
                          }`}
                        >
                          {price.change_percent >= 0 ? '↑' : '↓'}
                          {Math.abs(price.change_percent)?.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col text-xs text-gray-600">
                          <span className="text-success-600">高: ${price.high_price?.toFixed(2)}</span>
                          <span className="text-danger-600">低: ${price.low_price?.toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="text-sm text-gray-900 px-6 py-4">
                        {price.volume?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col text-xs">
                          <span className="text-gray-900">
                            {price.timestamp_tz
                              ? format(new Date(price.timestamp_tz), 'HH:mm:ss')
                              : '-'}
                          </span>
                          <span className="text-gray-500">
                            {price.timestamp_tz
                              ? formatDistanceToNow(new Date(price.timestamp_tz), {
                                  addSuffix: true,
                                  locale: zhCN,
                                })
                              : '-'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer flex items-center justify-between">
            <div className="text-sm text-gray-700">
              第 <span className="font-medium">{currentPage}</span> 页，
              共 <span className="font-medium">{totalPages}</span> 页
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                首页
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm rounded ${
                        currentPage === pageNum
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                末页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
